import { Button, Grid, NumberInput, Select, TextInput, Group, Checkbox, Text, Flex, Space, Textarea } from "@mantine/core";
import { useState, useImperativeHandle, useRef } from "react";
import { Expression, FormulaElement, FormulaType, InputElement, LookupData, Module, QueryKeys } from "../../models/models";
import { useQuery, useStore } from "../../stores/store";
import t, { wrappers } from "../../stores/language";
import { observer } from "mobx-react";
import Modal from "../../custom-components/model";
import React from "react";
import Monaco from "../../custom-components/monaco";
import { useParams } from "react-router-dom";
import { Icon } from "../../custom-components/icons";
import { faPencil, faTrash, faPlus } from "@fortawesome/pro-duotone-svg-icons";
import { useStyles } from "./configure-input";
import { TableData, TableView } from "../../custom-components/table";
import theme from "../../stores/theme";
import { supportedMathFunction } from "../../stores/constants";
import PopConfirm from "../../custom-components/pop-confirm";

export const getSortedData = (orders: string[], elements: InputElement[] | FormulaElement[] | TableData[] | Module[]) => {
  const orderedData = elements.sort((a, b) => {
    const indexA = orders.findIndex((p) => p === a.id?.toString());
    const indexB = orders.findIndex((p) => p === b.id?.toString());
    if (indexA === -1 && a.id != null) return 1;
    if (indexB === -1 && b.id != null) return -1;
    return indexA - indexB;
  });
  return orderedData;
};
export const ConfigureFormula = observer(function ConfigureFormula() {
  const { store } = useStore();
  const { classes } = useStyles();
  const formulaCreateRef = useRef<FormulaCreateHandle>(null);
  const { id } = useParams();
  const inputElements = store.GetInputByModule(id ?? "");
  const formulaElements = store.GetFormulasByModule(id ?? "");

  const deleteFormula = (id: number) => {
    store.DeleteFormula(id);
  };

  const inputElementsOrder = (store.GetModuleById(id ?? "0")?.moduleConfig?.inputElementsOrder ?? []).map((ie) => ie.toString());
  const orderedInputData = inputElementsOrder.length > 0 ? (getSortedData(inputElementsOrder, inputElements) as InputElement[]) : inputElements;

  const inputData = orderedInputData.map((s) => {
    const row: { [key: string]: string | null | number } = {};
    const keyValue = `${wrappers.openingWrapper}${s.name}${wrappers.closingWrapper}`;
    row.key = keyValue;
    row.name = s.name;
    row.type = "Input";
    row.group = s.inputConfig?.group ?? "";
    return row;
  });

  const formulaData = formulaElements.map((s) => {
    const row: { [key: string]: string | null | number } = {};
    Object.entries(s).forEach(([key, value]) => {
      row[key] = key !== "inputConfig" ? value.toString() : JSON.stringify(value);
    });
    return row;
  });

  const formulaElementsOrder = store.GetModuleById(id ?? "0")?.moduleConfig?.formulaElementsOrder;

  const reOrder = (orderedData: number[]) => {
    store.UpdateSortOrder(parseInt(id ?? "0"), "FormulaElements", orderedData);
  };

  const GetActions = (row: { [key: string]: string | null | number }) => {
    return (
      <Flex gap={theme.other?.arc.paddings[1]}>
        <Button
          size="xs"
          onClick={() => {
            formulaCreateRef.current?.show(row.id as number);
          }}
          variant="subtle"
          compact
          className={classes.iconButton}
          disabled={row.type == "Input" ? true : false}
        >
          <Icon icon={faPencil} size="xl" />
        </Button>
        <PopConfirm confirmText={t("Are You Sure?")} width={180} onConfirm={() => deleteFormula(row.id as number)}>
          <Button size="xs" variant="subtle" compact className={classes.iconButton} disabled={row.type == "Input" ? true : false}>
            <Icon icon={faTrash} size="xl" color={row.type != "Input" ? "red" : ""} />
          </Button>
        </PopConfirm>
      </Flex>
    );
  };

  return (
    <>
      <div className={classes.title}>
        <span>{t("Added Formula Elements")} </span>
        <Button size="xs" onClick={() => formulaCreateRef.current?.show(0)} className={classes.addIconButton}>
          {<Icon size="2x" icon={faPlus}></Icon>}
        </Button>
      </div>
      <TableView
        data={[...inputData, ...formulaData]}
        columns={[
          { key: "group", label: "Group", width: "10%" },
          { key: "key", label: "Key", width: "30%" },
          { key: "name", label: "Name", width: "30%" },
          { key: "comment", label: "Comment", width: "20%" },
          { key: "type", label: "Type", width: "10%" },
        ]}
        actions={GetActions}
        reOrder={reOrder}
        order={formulaElementsOrder}
      />
      <FormulaCreate ref={formulaCreateRef}></FormulaCreate>
    </>
  );
});

interface FormulaCreateHandle {
  show: (input: number) => void;
}
export const currentYear = new Date().getFullYear().toString();

const defaultFormula: FormulaElement = {
  id: 0,
  key: "",
  name: "",
  type: "Fixed Value",
  group: "",
  formulaConfig: {
    unitRate: 0,
    quantity: 0,
    expressions: [],
    showInCalculate: false,
    hideInDetailResults: false,
  },
  comment: "",
};
const defaultExpression: Expression = {
  id: "",
  condition: "",
  pageNo: "0",
  lookUpName: "",
  unitRate: "",
  quantity: "",
  lookupValueType: "Min",
  cost: "",
  year: currentYear,
};
const formulaExpressionType = ["QTY", "UNITRATE", "COST"];

const FormulaCreate = observer(
  React.forwardRef<FormulaCreateHandle, unknown>(function FormulaCreate(props, ref) {
    const { classes } = useStyles();
    const { store } = useStore();
    const [visible, setVisible] = useState<boolean>(false);
    const [expression, setExpression] = useState<Expression>(defaultExpression);
    const [formulaElement, setFormulaElement] = useState<FormulaElement>(defaultFormula);
    const { id } = useParams();
    const [validationError, setValidationError] = useState<string>("");
    const inputElements = store.GetInputByModule(id ?? "");
    const formulaElements = store.GetFormulasByModule(id ?? "");
    const lookupData = store.lookupData;

    const { data: lookupDataPageNo } = useQuery({
      queryKey: [QueryKeys.LookupDataPageNo],
      queryFn: async () => {
        return await store.GetAllLookupDataPageNo();
      },
    });

    useImperativeHandle(ref, () => {
      return {
        show: show,
      };
    });

    const show = (id: number) => {
      const formula = store.formulaElements.find((s) => s.id == id);
      setFormulaElement(formula ?? defaultFormula);
      setVisible(true);
      setExpression({ ...defaultExpression, cost: getDefaultCostFormula(formula?.key ?? "") });
    };

    const getDefaultCostFormula = (formulaName: string) => {
      return formulaName !== "" ? `${formulaName}(QTY) * ${formulaName}(UNITRATE)` : "";
    };

    const resetState = () => {
      setFormulaElement(defaultFormula);
      setExpression(defaultExpression);
    };
    const addFormula = () => {
      resetState();
      if (formulaElement.id === 0) {
        store.AddFormula({
          ...formulaElement,
          id: formulaElements.length + 1,
          moduleId: Number(id ?? "0"),
        });
      } else {
        store.UpdateFormula({ ...formulaElement, key: formulaElement.key });
      }
      setVisible(false);
    };
    const addExpressions = () => {
      if (expression.cost === "") {
        setValidationError("Cost formula is required");
        return;
      }
      setFormulaElement({
        ...formulaElement,
        formulaConfig: {
          ...formulaElement.formulaConfig,
          expressions: [...(formulaElement.formulaConfig?.expressions ?? []), { ...expression, id: `id_${Math.random().toString(15)}` }],
        },
      });
      setExpression({ ...defaultExpression, cost: getDefaultCostFormula(formulaElement.key ?? "") });
    };
    const updateExpression = () => {
      if (expression.cost === "") {
        setValidationError("Cost formula is required");
        return;
      }
      setFormulaElement({
        ...formulaElement,
        formulaConfig: {
          ...formulaElement.formulaConfig,
          expressions: [...(formulaElement.formulaConfig?.expressions ?? []).map((d) => (d.id === expression.id ? expression : d))],
        },
      });
      setExpression({ ...defaultExpression, cost: getDefaultCostFormula(formulaElement.key ?? "") });
    };
    const selectForEdit = (ex: Expression) => {
      setExpression({ ...ex, year: ex.year ?? currentYear });
    };
    const deleteExpression = (ex: Expression) => {
      setFormulaElement({
        ...formulaElement,
        formulaConfig: {
          ...formulaElement.formulaConfig,
          expressions: [...(formulaElement.formulaConfig?.expressions?.filter((d) => d.id != ex.id) ?? [])],
        },
      });
    };
    const updateSelectedExpression = (
      key: "condition" | "unitRate" | "quantity" | "lookUpName" | "pageNo" | "lookupValueType" | "cost" | "year",
      value: string
    ) => {
      switch (key) {
        case "lookUpName":
          setExpression({ ...expression, [key]: value, unitRate: getLookupValues(value, expression?.lookupValueType ?? "Min") });
          break;
        case "pageNo":
          {
            value === expression.pageNo
              ? setExpression({ ...expression, [key]: value })
              : setExpression({ ...expression, [key]: value, unitRate: "", lookUpName: "" });
          }
          break;
        case "lookupValueType":
          setExpression({ ...expression, [key]: value, unitRate: getLookupValues(expression?.lookUpName ?? "", value) });
          break;
        case "year":
          setExpression({ ...expression, [key]: value, pageNo: "", lookUpName: "" });
          break;
        default:
          setExpression({ ...expression, [key]: value.replaceAll("@", "") });
          setValidationError("");
          break;
      }
    };

    const getExpressionRows = () => {
      const expressions = formulaElement.formulaConfig?.expressions ?? [];
      const data = expressions.map((s) => {
        const row: { [key: string]: string | null | number } = {};
        Object.entries(s).forEach(([key, value]) => {
          row[key] = value?.toString();
        });
        return row;
      });
      return data;
    };

    const getTableColumns = (type: string) => {
      const columns = [
        { key: "condition", label: "Condition" },
        { key: "unitRate", label: type === "Look Up" ? "Look Up Formula" : "Unit Rate Formula" },
        { key: "quantity", label: "Qty Formula" },
        { key: "cost", label: "Cost Formula" },
      ];
      return columns;
    };
    const GetActions = (row: { [key: string]: string | null | number }) => {
      return (
        <Flex>
          <Button
            size="xs"
            onClick={() => {
              selectForEdit(row as Expression);
              getLookupData(row["pageNo"]?.toString() ?? "");
            }}
            variant="subtle"
            compact
            className={classes.iconButton}
          >
            <Icon icon={faPencil} size="xl" />
          </Button>
          <PopConfirm confirmText={t("Are You Sure?")} width={180} onConfirm={() => deleteExpression(row as Expression)}>
            <Button size="xs" variant="subtle" compact className={classes.iconButton}>
              <Icon icon={faTrash} size="xl" color="red" />
            </Button>
          </PopConfirm>
        </Flex>
      );
    };

    // To Get LookupData Based on Selected PageNo
    const getLookupData = async (pageNo: string) => {
      if (pageNo === "") return;
      await store.GetLookupDataByPageNo(pageNo, expression?.year ?? currentYear).then((data: LookupData[]) => {
        store.SetLookupData(data);
      });
    };

    // To Get Lookup Names for Selected - Reference (Page No)
    const getLookupNames = () => {
      let lookupNames: { value: string; label: string; group: string }[] = [];
      if (lookupData.length > 0) {
        const groupedLookup = lookupData.reduce((lookups: { [key: string]: LookupData[] }, lookup) => {
          const key = lookup.key;
          if (!lookups[key]) {
            lookups[key] = [];
          }
          lookups[key].push(lookup);
          return lookups;
        }, {});
        lookupNames = Object.keys(groupedLookup).map((key) => ({
          value: key,
          label: groupedLookup[key][0].name,
          group: groupedLookup[key][0].lookupGroup.toString(),
        }));
      }
      return lookupNames;
    };

    // To Show Lookup Values in Lookup Formula For Selected Lookup Name and Value Type
    const getLookupValues = (lookupKey: string, valueType: string) => {
      const lookupFormula = (lookupData ?? [])
        .filter((data) => data.key == lookupKey)
        .map((item) => {
          const lookupValue = getLookupValue(item.lookupValue, valueType);
          return `[${item.stateName}]: ${lookupValue}`;
        })
        .join("\r\n");
      return lookupFormula;
    };

    // To Get Lookup Value for Each Lookup Item in Expression Based on Selected Value Type
    const getLookupValue = (values: number[], lookupValueType: string) => {
      let value = 0;
      switch (lookupValueType) {
        case "Min":
          value = values.length > 1 ? Math.min(...values) : values[0];
          break;
        case "Max":
          value = values.length > 1 ? Math.max(...values) : values[0];
          break;
        case "Average":
          value = values.length > 1 ? (values[0] + values[1]) / 2 : values[0];
          break;
        case "Actual":
          value = values[0] ?? 0;
          break;
      }
      return value;
    };

    // To Get Available Group Names as Options in Group Dropdown
    const getFormulaGroups = () => {
      const formulas = formulaElements.filter((formula) => formula?.group !== "");
      if (formulaElement.group !== "") formulas.push(formulaElement);
      return [...new Set(formulas.map((fe) => fe.group ?? ""))].filter((d) => d !== "");
    };

    // To Get InputElements Dropdown Options Suggestion in Monaco Editor
    const inputElementsDropdownOptions = () => {
      let dropDownOptions: string[] = [];
      inputElements
        .filter((ie) => ie.type == "Dependent Dropdown" || ie.type == "Dropdown")
        .map((element) => {
          if (element.inputConfig?.options != null) {
            dropDownOptions = [...dropDownOptions, ...element.inputConfig.options];
          } else {
            (element.inputConfig?.dependentOptions ?? []).map((d) => {
              dropDownOptions = [...dropDownOptions, ...d.options];
            });
          }
        });
      return dropDownOptions;
    };

    const autoCompleate = [
      ...inputElements.map(
        (d) => `${wrappers.openingWrapper}${d.name}${wrappers.closingWrapper}${wrappers.openingParenthesis}INPUT${wrappers.closingParenthesis}`
      ),
      ...formulaElements.flatMap((d) => formulaExpressionType.map((type) => `${d.key}${wrappers.openingParenthesis}${type}${wrappers.closingParenthesis}`)),
      ...supportedMathFunction,
      ...inputElementsDropdownOptions().map((option) => `${wrappers.quote}${option}${wrappers.quote}`),
    ];

    const dependents = () => {
      switch (formulaElement.type) {
        case "Fixed Value":
          return (
            <>
              <Grid.Col span={6}>
                <NumberInput
                  onChange={(v) => {
                    setFormulaElement({ ...formulaElement, formulaConfig: { ...formulaElement.formulaConfig, unitRate: Number(v) } });
                  }}
                  value={formulaElement.formulaConfig?.unitRate ?? 0}
                  label={t("Unit Rate")}
                  precision={theme.other?.numberInput.precision}
                  step={theme.other?.numberInput.step}
                ></NumberInput>
              </Grid.Col>
              <Grid.Col span={6}>
                <NumberInput
                  onChange={(v) => {
                    setFormulaElement({ ...formulaElement, formulaConfig: { ...formulaElement.formulaConfig, quantity: Number(v) } });
                  }}
                  value={formulaElement.formulaConfig?.quantity ?? 0}
                  label={t("Quantity")}
                  precision={theme.other?.numberInput.precision}
                  step={theme.other?.numberInput.step}
                ></NumberInput>
              </Grid.Col>
            </>
          );
        //todo
        case "Look Up":
          return (
            <>
              <Grid.Col span={12}>
                <Grid>
                  <Grid.Col span={3}>
                    <Select
                      onChange={(value) => {
                        if (value == expression.pageNo) return;
                        updateSelectedExpression("pageNo", value ?? "");
                        getLookupData(value ?? "");
                      }}
                      value={expression.pageNo}
                      data={
                        lookupDataPageNo && lookupDataPageNo[expression?.year ?? currentYear]
                          ? lookupDataPageNo[expression?.year ?? currentYear].slice().sort((a, b) => parseInt(a) - parseInt(b))
                          : []
                      }
                      label={t("Page No")}
                      searchable
                      nothingFound={t("No page no found")}
                      maxDropdownHeight={150}
                    ></Select>
                  </Grid.Col>
                  <Grid.Col span={3}>
                    <Select
                      onChange={(value) => {
                        if (value == expression.year) return;
                        updateSelectedExpression("year", value ?? currentYear);
                      }}
                      value={expression.year}
                      data={lookupDataPageNo ? Object.keys(lookupDataPageNo).sort((a, b) => parseInt(a) - parseInt(b)) : []}
                      label={t("Year")}
                      searchable
                      nothingFound={t("No year found")}
                      maxDropdownHeight={150}
                    />
                  </Grid.Col>
                  <Grid.Col span={3}>
                    <Select
                      styles={() => ({
                        separatorLabel: {
                          color: theme.colors?.dark ? theme.colors.dark[3] : "black",
                          "&:: after": {
                            display: "none",
                          },
                        },
                        item: {
                          color: theme.colors?.gray ? theme.colors.gray[3] : "gray",
                        },
                      })}
                      filter={(v, item) => {
                        if (v.trim() === "") return true;
                        return (
                          (item.group?.toLocaleLowerCase().includes(v.toLocaleLowerCase()) ||
                            item.label?.toLocaleLowerCase().includes(v.toLocaleLowerCase())) ??
                          true
                        );
                      }}
                      onChange={(value) => {
                        updateSelectedExpression("lookUpName", value ?? "");
                      }}
                      value={expression.lookUpName}
                      data={expression.pageNo ? getLookupNames() : []}
                      label={t("Look Up Name")}
                      clearable={true}
                      nothingFound={t("No look up found")}
                      searchable
                    ></Select>
                  </Grid.Col>
                  <Grid.Col span={3}>
                    <Select
                      label={t("Look Up Value Type")}
                      clearable={false}
                      data={["Min", "Max", "Average", "Actual"]}
                      value={expression.lookupValueType}
                      onChange={(value) => {
                        updateSelectedExpression("lookupValueType", value ?? "Min");
                      }}
                    />
                  </Grid.Col>
                  <Grid.Col span={3}>
                    <Monaco
                      label={t("Condition")}
                      onChange={(value) => {
                        updateSelectedExpression("condition", value);
                      }}
                      value={expression.condition}
                      autoComplate={autoCompleate}
                    ></Monaco>
                  </Grid.Col>
                  <Grid.Col span={3}>
                    <Monaco
                      label={t("Look Up Formula")}
                      onChange={(value) => {
                        updateSelectedExpression("unitRate", value ?? "");
                      }}
                      value={expression.unitRate}
                      autoComplate={autoCompleate}
                    ></Monaco>
                  </Grid.Col>
                  <Grid.Col span={3}>
                    <Monaco
                      label={t("Qty Formula")}
                      onChange={(value) => {
                        updateSelectedExpression("quantity", value ?? "");
                      }}
                      value={expression.quantity}
                      autoComplate={autoCompleate}
                    ></Monaco>
                  </Grid.Col>
                  <Grid.Col span={2}>
                    <Monaco
                      label={t("Cost Formula")}
                      onChange={(value) => {
                        updateSelectedExpression("cost", value ?? "");
                      }}
                      value={expression.cost}
                      autoComplate={autoCompleate}
                      suggestionsPosition="left"
                    ></Monaco>
                    <Text color={"red"} size="xs">
                      {validationError}
                    </Text>
                  </Grid.Col>
                  <Grid.Col span={1} className={classes.gridAddButton}>
                    <Button
                      className={classes.addIconButton}
                      onClick={() => {
                        expression.id != "" ? updateExpression() : addExpressions();
                      }}
                    >
                      {<Icon size="2x" icon={faPlus} className="gridAddIcon"></Icon>}
                    </Button>
                  </Grid.Col>
                </Grid>
              </Grid.Col>

              {/* <Grid.Col span={6}>
                <Text fz="sm" fw={500} pb={2}>
                  {t("Page Preview")}
                </Text>
                <ScrollArea style={{ height: "230px" }}>
                  <img style={{ width: "100%", overflowX: "scroll" }} src={page}></img>
                </ScrollArea>
              </Grid.Col> */}
              <Grid.Col span={12}>
                <TableView
                  data={getExpressionRows()}
                  columns={getTableColumns("Look Up")}
                  actions={GetActions}
                  height={theme.other?.formula.expressionTable.height}
                />
              </Grid.Col>
            </>
          );
        //todo
        case "Formula":
          return (
            <>
              <Grid.Col span={3}>
                <Monaco
                  label={t("Condition")}
                  onChange={(value) => {
                    updateSelectedExpression("condition", value ?? "");
                  }}
                  value={expression.condition}
                  autoComplate={autoCompleate}
                ></Monaco>
              </Grid.Col>
              <Grid.Col span={3}>
                <Monaco
                  label={t("Unit Rate Formula")}
                  onChange={(value) => {
                    updateSelectedExpression("unitRate", value ?? "");
                  }}
                  value={expression.unitRate}
                  autoComplate={autoCompleate}
                ></Monaco>
              </Grid.Col>
              <Grid.Col span={3}>
                <Monaco
                  label={t("Qty Formula")}
                  onChange={(value) => {
                    updateSelectedExpression("quantity", value ?? "");
                  }}
                  value={expression.quantity}
                  autoComplate={autoCompleate}
                ></Monaco>
              </Grid.Col>
              <Grid.Col span={2}>
                <Monaco
                  label={t("Cost Formula")}
                  onChange={(value) => {
                    updateSelectedExpression("cost", value ?? "");
                  }}
                  value={expression.cost}
                  autoComplate={autoCompleate}
                  suggestionsPosition="left"
                ></Monaco>
                <Text color={"red"} size="xs">
                  {validationError}
                </Text>
              </Grid.Col>
              <Grid.Col className={classes.gridAddButton} span={1}>
                <Button
                  className={classes.addIconButton}
                  onClick={() => {
                    expression.id != "" ? updateExpression() : addExpressions();
                  }}
                >
                  {<Icon size="2x" icon={faPlus} className="gridAddIcon"></Icon>}
                </Button>
              </Grid.Col>
              <Grid.Col span={12}>
                <TableView data={getExpressionRows()} columns={getTableColumns("Formula")} actions={GetActions} height={"calc(100vh - 534px)"} />
              </Grid.Col>
            </>
          );
        default:
          return <></>;
      }
    };
    return (
      <>
        <Modal
          className={classes.configureModal}
          opened={visible}
          header={formulaElement.id === 0 ? t("Add Formula Element") : t(`Update ${formulaElement.name}`)}
          footer={
            <Group
              className={classes.modalFooter}
              style={{
                paddingTop: "8px",
                marginTop: "auto",
                position: "sticky",
                bottom: 0,
                backgroundColor: "white",
                zIndex: 10,
              }}
            >
              <Button
                variant={"default"}
                onClick={() => {
                  setVisible(false);
                  resetState();
                }}
              >
                {t("Cancel")}
              </Button>
              <Button onClick={() => addFormula()}>{formulaElement.id === 0 ? t("Add") : t("Update")}</Button>
            </Group>
          }
          withCloseButton={true}
          onClose={() => {
            setVisible(false);
            resetState();
          }}
          size={"80%"}
        >
          <Grid columns={12} className={classes.inputContainer} align="center">
            <Grid.Col span={3}>
              <TextInput label={t("Key")} value={formulaElement.key} disabled />
            </Grid.Col>
            <Grid.Col span={3}>
              <TextInput
                label={t("Display Name")}
                value={formulaElement.name}
                onChange={(e) => {
                  if (formulaElement.id === 0) {
                    setFormulaElement({
                      ...formulaElement,
                      name: e.target.value,
                      key: `${wrappers.openingWrapper}${e.target.value}${wrappers.closingWrapper}`,
                    });
                    setExpression({ ...expression, cost: getDefaultCostFormula(`${wrappers.openingWrapper}${e.target.value}${wrappers.closingWrapper}`) });
                  } else {
                    setFormulaElement({ ...formulaElement, name: e.target.value });
                  }
                }}
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <>
                <Select
                  label={t("Group")}
                  data={getFormulaGroups()}
                  creatable
                  searchable
                  clearable
                  getCreateLabel={(query) => `+ Add ${query}`}
                  onCreate={(query) => {
                    setFormulaElement({ ...formulaElement, group: query });
                    return query;
                  }}
                  value={formulaElement.group}
                  onChange={(e) => setFormulaElement({ ...formulaElement, group: e ?? "" })}
                />
              </>
            </Grid.Col>
            <Grid.Col span={2}>
              <Select
                label={t("Type")}
                clearable={false}
                data={["Fixed Value", "Look Up", "Formula"]}
                value={formulaElement.type}
                onChange={(value: FormulaType) => setFormulaElement({ ...formulaElement, type: value ?? "Fixed Value" })}
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <Space h={20} />
              <Group spacing={5}>
                <Checkbox
                  label={t("Show In Calculate")}
                  checked={formulaElement.formulaConfig?.showInCalculate ?? false}
                  onChange={(event) =>
                    setFormulaElement({ ...formulaElement, formulaConfig: { ...formulaElement.formulaConfig, showInCalculate: event.currentTarget.checked } })
                  }
                  size={"xs"}
                />
                <Checkbox
                  label={t("Hide In Detail")}
                  checked={formulaElement.formulaConfig?.hideInDetailResults ?? false}
                  onChange={(event) =>
                    setFormulaElement({
                      ...formulaElement,
                      formulaConfig: { ...formulaElement.formulaConfig, hideInDetailResults: event.currentTarget.checked },
                    })
                  }
                  size={"xs"}
                />
              </Group>
            </Grid.Col>
            {dependents()}
            <Grid.Col span={12}>
              <Textarea
                label={t("Comment")}
                value={formulaElement.comment ?? ""}
                onChange={(e) => {
                  setFormulaElement({
                    ...formulaElement,
                    comment: e.target.value,
                  });
                }}
                autosize
                minRows={2}
                maxRows={2}
              />
            </Grid.Col>
          </Grid>
        </Modal>
      </>
    );
  })
);
